<template>
  <div>
    <process-add
      :is-sidebar-active.sync="isAddNewProcessSidebarActive"
      :is-readonly.sync="isAddNewProcessSidebarInDisplayMode"
      :value.sync="activeRecord"
      :edit-tab="editRecord"
    />
    <ImportCsvModal
      entity="process"
      title="Import Task Templates"
      @import-success="onImportSuccess"
    />

    <!-- Table Container Card -->
    <div no-body class="mb-0 container-card">
      <div class="m-2">
        <!-- Table Top -->
        <b-row>
          <!-- Search & Controls -->
          <b-col cols="12" md="12">
            <div class="d-flex align-items-center justify-content-between flex-wrap toolbar-container">
              <!-- Left Side: Dropdowns and Search Bar (Expands to fill available space) -->
              <div class="d-flex align-items-center flex-grow-1 flex-wrap my-50 toolbar-left">
                <!-- Show Selector -->
                <div class="d-flex align-items-center mr-1 my-25 flex-shrink-0">
                  <span class="text-muted font-small-3 mr-50 text-nowrap">Show</span>
                  <v-select
                    v-model="showMine"
                    :options="[
                      { label: 'All templates', value: false },
                      { label: 'My templates', value: true },
                    ]"
                    :reduce="(option) => option.value"
                    :clearable="false"
                    style="min-width: 140px"
                  />
                </div>

                <!-- Group by Selector -->
                <div class="d-flex align-items-center mr-1 my-25 flex-shrink-0">
                  <span class="text-muted font-small-3 mr-50 text-nowrap">Group by</span>
                  <v-select
                    v-model="groupBy"
                    :options="[
                      { label: 'Category', value: 'Category' },
                      { label: 'None', value: 'None' },
                    ]"
                    :reduce="(option) => option.value"
                    :clearable="false"
                    style="min-width: 130px"
                  />
                </div>

                <!-- Entity Filter (Full Width) -->
                <div class="my-25 mr-1 filter-wrapper flex-grow-1">
                  <transition name="slide-fade">
                    <entity-filter
                      entity="process"
                      entity-plural-slug="task templates"
                      :entity-field-suggestions="PROCESS_ENTITY_FIELD_SUGGESTIONS"
                      :search-query.sync="searchQuery"
                      :selected-queries.sync="selectedQueries"
                      @clear-query="clearQuery"
                    />
                  </transition>
                </div>
              </div>

              <!-- Right Side: Action Buttons -->
              <div class="d-flex align-items-center my-50 flex-shrink-0 toolbar-right">
                <transition name="slide-fade">
                  <div v-if="selectedItems.length !== 0">
                    <b-button
                      class="mr-1"
                      variant="danger"
                      @click="deleteSelectedRecords"
                    >
                      <span class="text-nowrap">Delete ({{ selectedItems.length }})</span>
                    </b-button>
                  </div>
                </transition>
                <b-button
                  v-if="$can('import', 'process')"
                  variant="outline-primary"
                  class="import-btn mr-1 d-inline-flex align-items-center"
                  @click="openImportCsvModal()"
                >
                  <feather-icon icon="UploadIcon" class="mr-50" />
                  <span>Import</span>
                </b-button>
                <b-button
                  v-if="$can('import', 'process')"
                  variant="outline-primary"
                  class="import-btn mr-1 d-inline-flex align-items-center"
                  @click="exportCsv()"
                >
                  <feather-icon icon="DownloadIcon" class="mr-50" />
                  <span>Export</span>
                </b-button>
                <b-button
                  v-if="$can('add', 'process') || $can('add', 'myProcess')"
                  variant="primary"
                  class="import-btn d-inline-flex align-items-center"
                  @click="editRecord(null)"
                >
                  <feather-icon icon="PlusIcon" class="mr-50" />
                  <span>New Task Template</span>
                </b-button>
              </div>
            </div>
          </b-col>
        </b-row>
      </div>

      <!-- Loading Spinner -->
      <div v-if="isLoading" class="d-flex justify-content-center py-5">
        <b-spinner type="grow" variant="primary" label="Loading..." />
      </div>

      <!-- Empty State -->
      <div
        v-else-if="allRawProcesses.length === 0"
        class="text-center py-4 text-muted"
      >
        No matching records found
      </div>

      <!-- Grouped by Category View -->
      <div v-else-if="groupBy === 'Category'">
        <div
          v-for="(group, groupIndex) in groupedCategories"
          :key="group.id"
          class="category-group-section"
        >
          <!-- Category Header Bar -->
          <div
            class="category-group-header"
            :class="{ 'is-first': groupIndex === 0 }"
            @click="toggleCategory(group.id)"
          >
            <feather-icon
              :icon="isCategoryCollapsed(group.id) ? 'ChevronRightIcon' : 'ChevronDownIcon'"
              size="18"
              class="mr-50 text-body cursor-pointer flex-shrink-0"
            />
            <span class="category-prefix-badge">{{ group.prefix }}</span>
            <span class="category-title font-weight-bold ml-25 mr-50 text-body">{{ group.description }}</span>
            <span class="category-count">
              {{ group.items.length }} {{ group.items.length === 1 ? 'template' : 'templates' }}
            </span>
          </div>

          <!-- Category Table (Collapsible) -->
          <b-collapse :visible="!isCategoryCollapsed(group.id)">
            <b-table
              class="position-relative table process-table"
              :items="getDisplayedItems(group)"
              responsive
              :fields="tableColumns"
              primary-key="id"
              :no-local-sorting="true"
              hover
              @row-clicked="displayRecord"
            >
              <!-- Column: Select -->
              <template #cell(select)="data">
                <div @click.stop>
                  <b-form-checkbox
                    class="m-0"
                    :checked="isItemSelected(data.item)"
                    @change="selectOrUnselectRow($event, data.item)"
                  />
                </div>
              </template>

              <!-- Column: ID -->
              <template #cell(processHandle)="data">
                <span class="text-muted font-weight-medium">{{ data.item.processHandle }}</span>
              </template>

              <!-- Column: Name -->
              <template #cell(name)="data">
                <span class="font-weight-medium text-body">{{ data.item.name }}</span>
              </template>

              <!-- Column: Resources (Docs) -->
              <template #cell(resources)="data">
                <div
                  v-if="data.item.docs && data.item.docs.length > 0"
                  class="d-inline-flex align-items-center"
                  @click.stop
                >
                  <feather-icon
                    :id="`resource-popover-${data.item.id}`"
                    icon="FileTextIcon"
                    size="16"
                    class="cursor-pointer text-primary"
                  />
                  <b-popover
                    :target="`resource-popover-${data.item.id}`"
                    triggers="hover focus"
                    placement="bottom"
                    custom-class="resource-preview-popover shadow-sm"
                  >
                    <div class="resource-popover-content">
                      <div class="text-muted font-small-2 mb-50 font-weight-bolder">
                        {{ data.item.docs.length }} {{ data.item.docs.length === 1 ? 'resource' : 'resources' }}
                      </div>
                      <div class="resource-links-list">
                        <a
                          v-for="doc in data.item.docs.slice(0, 3)"
                          :key="doc.id"
                          :href="doc.value"
                          target="_blank"
                          class="d-flex align-items-center py-25 text-primary text-decoration-none resource-item-link"
                          @click.stop
                        >
                          <feather-icon
                            :icon="isExternalLink(doc.value) ? 'ExternalLinkIcon' : 'FileTextIcon'"
                            size="13"
                            class="mr-50 flex-shrink-0"
                          />
                          <span class="text-truncate font-small-2">{{ doc.name || doc.value }}</span>
                        </a>
                        <div
                          v-if="data.item.docs.length > 3"
                          class="text-muted font-small-2 mt-25"
                        >
                          +{{ data.item.docs.length - 3 }} more resources
                        </div>
                      </div>
                      <div class="text-muted font-small-1 mt-50 pt-50 border-top">
                        Click the row to open the template
                      </div>
                    </div>
                  </b-popover>
                </div>
              </template>

              <!-- Column: Frequency -->
              <template #cell(recurringMonthsBitmask)="data">
                <span
                  v-if="getOverlayText(data.item.frequency)"
                  class="frequency-plain-text"
                >
                  {{ getOverlayText(data.item.frequency) }}
                </span>
                <ProcessFrequencyTiles
                  v-else-if="
                    data.item.frequencyType === 'Scheduled' &&
                    data.item.frequency &&
                    frequencyKeys(Object.keys(data.item.frequency), data.item.frequency)
                  "
                  :recurring-months="getRecurringMonths(data.item.frequency, currentYear).months"
                />
                <span
                  v-else-if="data.item.frequencyType === 'Scheduled'"
                  class="frequency-plain-text"
                >
                  Dates not set
                </span>
                <span
                  v-else-if="data.item.frequencyType === 'AsNeeded'"
                  class="frequency-plain-text"
                >
                  As Needed
                </span>
                <span v-else class="frequency-plain-text">--</span>
              </template>

              <!-- Column: Role -->
              <template #cell(role)="data">
                <span v-if="data.item.role && data.item.role.roleHandle" class="role-chip">
                  {{ data.item.role.roleHandle }}
                </span>
                <span
                  v-else-if="data.item.assignedRoles && data.item.assignedRoles.length > 0"
                  class="d-inline-flex flex-wrap"
                  style="gap: 4px;"
                >
                  <span
                    v-for="r in data.item.assignedRoles.slice(0, 2)"
                    :key="r.id"
                    class="role-chip"
                  >
                    {{ r.roleHandle }}
                  </span>
                  <span v-if="data.item.assignedRoles.length > 2" class="role-chip">
                    +{{ data.item.assignedRoles.length - 2 }}
                  </span>
                </span>
                <span v-else class="text-muted font-small-2">--</span>
              </template>

              <!-- Column: Labels -->
              <template #cell(labels)="data">
                <label-chip :labels="data.item.labels" />
              </template>

              <!-- Column: Actions (⋮ Kebab Menu) -->
              <template #cell(actions)="data">
                <div class="d-flex justify-content-end align-items-center" @click.stop>
                  <b-dropdown
                    variant="link"
                    no-caret
                    right
                    class="row-actions-dropdown"
                    append-to-body
                  >
                    <template #button-content>
                      <feather-icon
                        icon="MoreVerticalIcon"
                        size="16"
                        class="text-body cursor-pointer"
                      />
                    </template>
                    <b-dropdown-item
                      v-if="$can('delete', 'process')"
                      @click.stop="deleteRecord(data.item)"
                    >
                      <span class="align-middle text-danger">
                        <feather-icon icon="TrashIcon" size="14" class="mr-50" />
                        Delete
                      </span>
                    </b-dropdown-item>
                  </b-dropdown>
                </div>
              </template>
            </b-table>
            <!-- Show N More Button -->
            <div
              v-if="isCategoryInPreview(group)"
              class="show-more-row text-center py-50 border-top cursor-pointer font-weight-medium"
              @click="expandCategoryFully(group.id)"
            >
              <feather-icon icon="ChevronDownIcon" size="14" class="mr-25" />
              Show {{ group.items.length - 2 }} more
            </div>
          </b-collapse>
        </div>

        <!-- Summary Footer for Grouped View -->
        <div class="px-2 py-1 border-top">
          <span class="text-muted font-small-3">
            {{ allRawProcesses.length }} {{ allRawProcesses.length === 1 ? 'template' : 'templates' }} in {{ groupedCategories.length }} {{ groupedCategories.length === 1 ? 'category' : 'categories' }}
          </span>
        </div>
      </div>

      <!-- Flat Table (Group by: None) -->
      <div v-else>
        <b-table
          id="table"
          ref="refProcessListTable"
          class="position-relative table process-table"
          :items="allRawProcesses"
          responsive
          :fields="tableColumns"
          primary-key="id"
          :sort-by.sync="sortBy"
          :sort-desc.sync="isSortDirDesc"
          :no-local-sorting="true"
          hover
          @row-clicked="displayRecord"
        >
          <!-- Column: Select -->
          <template #cell(select)="data">
            <div @click.stop>
              <b-form-checkbox
                class="m-0"
                :checked="isItemSelected(data.item)"
                @change="selectOrUnselectRow($event, data.item)"
              />
            </div>
          </template>

          <!-- Column: ID -->
          <template #cell(processHandle)="data">
            <span class="text-muted font-weight-medium">{{ data.item.processHandle }}</span>
          </template>

          <!-- Column: Name -->
          <template #cell(name)="data">
            <span class="font-weight-medium text-body">{{ data.item.name }}</span>
          </template>

          <!-- Column: Resources (Docs) -->
          <template #cell(resources)="data">
            <div
              v-if="data.item.docs && data.item.docs.length > 0"
              class="d-inline-flex align-items-center"
              @click.stop
            >
              <feather-icon
                :id="`flat-resource-popover-${data.item.id}`"
                icon="FileTextIcon"
                size="16"
                class="cursor-pointer text-primary"
              />
              <b-popover
                :target="`flat-resource-popover-${data.item.id}`"
                triggers="hover focus"
                placement="bottom"
                custom-class="resource-preview-popover shadow-sm"
              >
                <div class="resource-popover-content">
                  <div class="text-muted font-small-2 mb-50 font-weight-bolder">
                    {{ data.item.docs.length }} {{ data.item.docs.length === 1 ? 'resource' : 'resources' }}
                  </div>
                  <div class="resource-links-list">
                    <a
                      v-for="doc in data.item.docs.slice(0, 3)"
                      :key="doc.id"
                      :href="doc.value"
                      target="_blank"
                      class="d-flex align-items-center py-25 text-primary text-decoration-none resource-item-link"
                      @click.stop
                    >
                      <feather-icon
                        :icon="isExternalLink(doc.value) ? 'ExternalLinkIcon' : 'FileTextIcon'"
                        size="13"
                        class="mr-50 flex-shrink-0"
                      />
                      <span class="text-truncate font-small-2">{{ doc.name || doc.value }}</span>
                    </a>
                    <div
                      v-if="data.item.docs.length > 3"
                      class="text-muted font-small-2 mt-25"
                    >
                      +{{ data.item.docs.length - 3 }} more resources
                    </div>
                  </div>
                  <div class="text-muted font-small-1 mt-50 pt-50 border-top">
                    Click the row to open the template
                  </div>
                </div>
              </b-popover>
            </div>
          </template>

          <!-- Column: Frequency -->
          <template #cell(recurringMonthsBitmask)="data">
            <span
              v-if="getOverlayText(data.item.frequency)"
              class="frequency-plain-text"
            >
              {{ getOverlayText(data.item.frequency) }}
            </span>
            <ProcessFrequencyTiles
              v-else-if="
                data.item.frequencyType === 'Scheduled' &&
                data.item.frequency &&
                frequencyKeys(Object.keys(data.item.frequency), data.item.frequency)
              "
              :recurring-months="getRecurringMonths(data.item.frequency, currentYear).months"
            />
            <span
              v-else-if="data.item.frequencyType === 'Scheduled'"
              class="frequency-plain-text"
            >
              Dates not set
            </span>
            <span
              v-else-if="data.item.frequencyType === 'AsNeeded'"
              class="frequency-plain-text"
            >
              As Needed
            </span>
            <span v-else class="frequency-plain-text">--</span>
          </template>

          <!-- Column: Role -->
          <template #cell(role)="data">
            <span v-if="data.item.role && data.item.role.roleHandle" class="role-chip">
              {{ data.item.role.roleHandle }}
            </span>
            <span
              v-else-if="data.item.assignedRoles && data.item.assignedRoles.length > 0"
              class="d-inline-flex flex-wrap"
              style="gap: 4px;"
            >
              <span
                v-for="r in data.item.assignedRoles.slice(0, 2)"
                :key="r.id"
                class="role-chip"
              >
                {{ r.roleHandle }}
              </span>
              <span v-if="data.item.assignedRoles.length > 2" class="role-chip">
                +{{ data.item.assignedRoles.length - 2 }}
              </span>
            </span>
            <span v-else class="text-muted font-small-2">--</span>
          </template>

          <!-- Column: Labels -->
          <template #cell(labels)="data">
            <label-chip :labels="data.item.labels" />
          </template>

          <!-- Column: Actions (⋮ Kebab Menu) -->
          <template #cell(actions)="data">
            <div class="d-flex justify-content-end align-items-center" @click.stop>
              <b-dropdown
                variant="link"
                no-caret
                right
                class="row-actions-dropdown"
                append-to-body
              >
                <template #button-content>
                  <feather-icon
                    icon="MoreVerticalIcon"
                    size="16"
                    class="text-body cursor-pointer"
                  />
                </template>
                <b-dropdown-item
                  v-if="$can('delete', 'process')"
                  @click.stop="deleteRecord(data.item)"
                >
                  <span class="align-middle text-danger">
                    <feather-icon icon="TrashIcon" size="14" class="mr-50" />
                    Delete
                  </span>
                </b-dropdown-item>
              </b-dropdown>
            </div>
          </template>
        </b-table>

        <!-- Pagination Footer for Flat View -->
        <div class="mx-2 mb-2 mt-1">
          <b-row>
            <b-col
              cols="12"
              sm="4"
              class="d-flex align-items-center justify-content-center justify-content-sm-start mb-1 mb-sm-0"
            >
              <span class="text-muted font-small-3">
                Showing {{ paginationMeta.from }} to {{ paginationMeta.to }} of
                {{ paginationMeta.of }} entries
              </span>
            </b-col>

            <!-- Pagination -->
            <b-col
              cols="12"
              sm="4"
              class="d-flex align-items-center justify-content-center mb-1 mb-sm-0"
            >
              <b-pagination
                v-model="currentPage"
                :total-rows="showMine ? currentUserProcesses.totalCount : processes.totalCount"
                :per-page="perPage"
                first-number
                last-number
                class="mb-0"
                prev-class="prev-item"
                next-class="next-item"
              >
                <template #prev-text>
                  <feather-icon icon="ChevronLeftIcon" size="18" />
                </template>
                <template #next-text>
                  <feather-icon icon="ChevronRightIcon" size="18" />
                </template>
              </b-pagination>
            </b-col>

            <!-- Per Page Selector -->
            <b-col
              cols="12"
              sm="4"
              class="d-flex align-items-center justify-content-center justify-content-sm-end"
            >
              <label class="mb-0 mr-50 font-small-3 text-muted">Show</label>
              <v-select
                v-model="perPage"
                append-to-body
                :calculate-position="withPopper"
                :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                :options="perPageOptions"
                :clearable="false"
                class="per-page-selector d-inline-block mx-25 vue-select-up"
              />
              <label class="mb-0 ml-50 font-small-3 text-muted">entries</label>
            </b-col>
          </b-row>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import ProcessFrequencyTiles from "./ProcessFrequencyTiles.vue"
import { getRecurringMonths } from "pbshared"
import { areArraysEqual } from "../../utils/object-util"
import {
  BRow,
  BCol,
  BButton,
  BTable,
  BPagination,
  BTooltip,
  BSpinner,
  BBadge,
  VBTooltip,
  BPopover,
  BFormCheckbox,
  BDropdown,
  BDropdownItem,
  BCollapse,
  VBPopover,
} from "bootstrap-vue"
import vSelect from "vue-select"
import Ripple from "vue-ripple-directive"
import { debounce } from "vue-debounce"
import { mapState, mapActions } from "vuex"
import { selectPosition } from "@core/mixins/ui/select"
import ImportCsvModal from "@/views/shared/ImportCsvModal.vue"
import { PROCESS_ENTITY_FIELD_SUGGESTIONS } from "@/utils/entity-filter"
import EntityFilter from "@/views/shared/Filter.vue"
import ProcessAdd from "./ProcessAdd.vue"
import moment from "moment-timezone"

export default {
  components: {
    ProcessFrequencyTiles,
    ProcessAdd,
    ImportCsvModal,
    EntityFilter,
    BPopover,
    BRow,
    BCol,
    BButton,
    BTable,
    BPagination,
    BTooltip,
    BSpinner,
    BBadge,
    BFormCheckbox,
    BDropdown,
    BDropdownItem,
    BCollapse,
    vSelect,
  },
  directives: {
    Ripple,
    "b-tooltip": VBTooltip,
    "b-popover": VBPopover,
  },
  mixins: [selectPosition],
  data() {
    const userType =
      this.$store?.state?.user?.currentUser?.userType ||
      this.$store?.state?.auth?.role
    const isAdminOrManager =
      userType?.toLowerCase() === "admin" ||
      userType?.toLowerCase() === "manager"
    const defaultShowMine = !isAdminOrManager

    return {
      currentYear: moment().year(),
      groupBy: "Category",
      categoryStates: {},
      tableColumns: [
        {
          key: "select",
          label: "",
          thClass: "select-th",
          tdClass: "select-td",
        },
        {
          key: "processHandle",
          sortable: true,
          label: "ID",
          thClass: "id-th",
          tdClass: "id-td",
        },
        {
          key: "name",
          sortable: true,
          label: "Name",
          thClass: "name-th",
          tdClass: "name-td",
        },
        {
          key: "resources",
          sortable: false,
          label: "",
          thClass: "resources-th",
          tdClass: "resources-td",
        },
        {
          key: "recurringMonthsBitmask",
          label: "Frequency",
          sortable: true,
          thClass: "frequency-th",
          tdClass: "frequency-td",
        },
        {
          key: "role",
          sortable: false,
          label: "Role",
          thClass: "role-th",
          tdClass: "role-td",
        },
        {
          key: "labels",
          sortable: false,
          label: "Labels",
          thClass: "labels-th",
          tdClass: "labels-td",
        },
        {
          key: "actions",
          label: "",
          thClass: "actions-th",
          tdClass: "actions-td",
          sortable: false,
        },
      ],
      isAddNewProcessSidebarActive: false,
      isAddNewProcessSidebarInDisplayMode: false,
      activeRecord: null,
      searchFilter: {
        processType: null,
      },
      perPageOptions: [10, 25, 50, 100],
      perPage: 10,
      currentPage: 1,

      sortBy: "recurringMonthsBitmask",
      isSortDirDesc: false,

      // FOR AUTO SUGGEST
      PROCESS_ENTITY_FIELD_SUGGESTIONS,
      searchQuery: [],
      selectedQueries: [],

      // For selecting table rows
      selectedItems: [],
      showMine: defaultShowMine,
    }
  },
  computed: {
    ...mapState("user", ["currentUser"]),
    ...mapState("process", [
      "processes",
      "currentUserProcesses",
      "isLoading",
      "fieldValues",
    ]),
    ...mapState("filter", ["filtersAccessibleToCurrentUser"]),
    isAdminOrManager() {
      const userType =
        this.currentUser?.userType || this.$store.state.auth?.role
      return (
        userType?.toLowerCase() === "admin" ||
        userType?.toLowerCase() === "manager"
      )
    },
    defaultShowMine() {
      return !this.isAdminOrManager
    },
    allRawProcesses() {
      const p = this.showMine ? this.currentUserProcesses : this.processes
      return (p && p.nodes) ? p.nodes : []
    },
    groupedCategories() {
      const list = this.allRawProcesses || []
      const groupsMap = {}
      const groupsOrder = []

      list.forEach((item) => {
        const cat = item.cat
        const catId = cat ? cat.id : "uncategorized"
        const prefix = cat ? (cat.prefix || "OTHER") : "OTHER"
        const description = cat ? (cat.description || cat.prefix || "Uncategorized") : "Uncategorized"

        if (!groupsMap[catId]) {
          groupsMap[catId] = {
            id: catId,
            prefix,
            description,
            items: [],
          }
          groupsOrder.push(groupsMap[catId])
        }
        groupsMap[catId].items.push(item)
      })

      return groupsOrder
    },
    paginationMeta() {
      let processesToPaginate = { nodes: [], totalCount: 0 }
      if (!this.showMine) processesToPaginate = this.processes
      else processesToPaginate = this.currentUserProcesses
      return {
        from:
          this.perPage * (this.currentPage - 1) +
          (processesToPaginate.nodes.length ? 1 : 0),
        to:
          this.perPage * (this.currentPage - 1) +
          processesToPaginate.nodes.length,
        of: processesToPaginate.totalCount,
      }
    },
    queryParams() {
      const isGrouped = this.groupBy === "Category"
      return {
        processType: this.searchFilter.processType,
        q: this.searchQuery,
        offset: isGrouped ? 0 : this.perPage * (this.currentPage - 1),
        limit: isGrouped ? 500 : this.perPage,
        sortBy: this.sortBy,
        isDesc: this.isSortDirDesc,
      }
    },
    currentRouteName() {
      return this.$route.name
    },
    isSelectAllChecked: {
      get() {
        return (
          this.allRawProcesses.length > 0 &&
          this.allRawProcesses.length === this.selectedItems.length
        )
      },
      set(newValue) {
        this.selectOrUnselectAllRows(newValue)
      },
    },
  },
  watch: {
    queryParams: {
      handler() {
        this.fetchProcesses()
      },
      deep: true,
    },
    groupBy() {
      this.currentPage = 1
      this.fetchProcesses()
    },
    currentPage() {
      this.fetchProcesses()
    },
    perPage() {
      this.currentPage = 1
      this.fetchProcesses()
    },
    currentRouteName() {
      this.$store.dispatch("process/resetProcesses")
      this.fetchProcesses()
    },
    showMine(newVal) {
      this.$store.dispatch("process/resetProcesses")
      this.fetchProcesses()

      const query = { ...this.$route.query }
      const mineStr = newVal ? "true" : "false"
      if (query.mine !== mineStr) {
        query.mine = mineStr
        this.$router.replace({ query }).catch(() => {})
      }
    },
    currentUser: {
      handler() {
        if (this.$route.query.mine === undefined) {
          this.showMine = this.defaultShowMine
        }
      },
    },
    $route: {
      handler(to) {
        const query = to.query

        if (query.mine !== undefined) {
          const isMine = query.mine === "true" || query.mine === true
          if (this.showMine !== isMine) {
            this.showMine = isMine
          }
        } else if (this.showMine !== this.defaultShowMine) {
          this.showMine = this.defaultShowMine
        }

        let assignQuery = []
        if (query.q) {
          assignQuery = Array.isArray(query.q) ? query.q : [query.q]
        } else if (query.id) {
          assignQuery = [`id=${query.id}`]
        }

        if (!areArraysEqual(this.selectedQueries, assignQuery)) {
          this.searchQuery = assignQuery
          this.selectedQueries = assignQuery
        }
      },
      deep: true,
    },
    processes: {
      handler() {
        const { compact, id } = this.$route.query
        if (compact && this.processes.nodes[0]) {
          this.displayRecord(this.processes.nodes[0])
        }
        if (id && this.selectedQueries.includes(`id=${id}`) && this.processes.nodes[0]) {
          const process = this.processes.nodes.find(p => p.id === parseInt(id))
          if (process) {
            this.selectedQueries = [process.name]
          }
        }
      },
    },
    currentUserProcesses: {
      handler() {
        const { compact, id } = this.$route.query
        if (compact && this.currentUserProcesses.nodes[0]) {
          this.displayRecord(this.currentUserProcesses.nodes[0])
        }
        if (id && this.selectedQueries.includes(`id=${id}`) && this.currentUserProcesses.nodes[0]) {
          const process = this.currentUserProcesses.nodes.find(p => p.id === parseInt(id))
          if (process) {
            this.selectedQueries = [process.name]
          }
        }
      },
    },
  },
  created() {
    const { mine } = this.$route.query
    if (mine !== undefined) {
      this.showMine = mine === "true" || mine === true
    } else {
      this.showMine = this.defaultShowMine
    }
    this.onLoad()
  },
  methods: {
    getRecurringMonths,
    getCategoryState(catId) {
      return this.categoryStates[catId] || 'preview'
    },
    isCategoryCollapsed(catId) {
      return this.getCategoryState(catId) === 'collapsed'
    },
    isCategoryInPreview(group) {
      return this.getCategoryState(group.id) === 'preview' && group.items.length > 2
    },
    getDisplayedItems(group) {
      const state = this.getCategoryState(group.id)
      if (state === 'collapsed') return []
      if (state === 'preview') return group.items.slice(0, 2)
      return group.items
    },
    toggleCategory(catId) {
      const current = this.getCategoryState(catId)
      if (current === 'collapsed') {
        this.$set(this.categoryStates, catId, 'full')
      } else {
        this.$set(this.categoryStates, catId, 'collapsed')
      }
    },
    expandCategoryFully(catId) {
      this.$set(this.categoryStates, catId, 'full')
    },
    isExternalLink(url) {
      return url && (/^https?:\/\//i.test(url) || url.startsWith("www."))
    },
    getOverlayText(frequency) {
      if (!frequency?.Interval?.RepeatEvery) return
      const r = frequency?.Interval?.RepeatEvery
      return `Every ${r === 1 ? "week" : r + " weeks"}`
    },
    ...mapActions("process", [
      "getProcesses",
      "getCurrentUserProcesses",
      "getPossibleValuesForFieldFromProcessEntity",
      "getProcessesStatsItems",
    ]),
    ...mapActions("filter", ["getFiltersAccessibleToCurrentUser"]),
    refetchData: debounce(function cb() {
      this.fetchProcesses()
    }, "300ms"),
    onLoad() {
      let assignQuery = []
      const { q, id } = this.$route.query

      if (q) {
        if (Array.isArray(q)) {
          assignQuery = q
        } else {
          assignQuery = [q]
        }

        this.searchQuery = assignQuery
        this.selectedQueries = assignQuery
      } else if (id) {
        assignQuery = [`id=${id}`]
        this.searchQuery = assignQuery
        this.selectedQueries = assignQuery
      }

      this.fetchProcesses()

      this.suggestions = this.fieldSuggestions
      this.getFiltersAccessibleToCurrentUser({
        associatedEntities: ["Process"],
      })
    },
    fetchProcesses() {
      if (!this.sortBy) {
        this.$nextTick(() => {
          this.sortBy = "processHandle"
          this.isSortDirDesc = false
        })
        return
      }

      if (!this.showMine) {
        this.getProcesses(this.queryParams)
      } else {
        this.getCurrentUserProcesses(this.queryParams)
      }
    },
    frequencyKeys(keys, parentArr) {
      let isValue = false
      keys.forEach((key) => {
        const innerObj = parentArr[key]
        if (innerObj) {
          const innerValues = Object.values(innerObj)
          innerValues.forEach((value) => {
            if (value && value.length > 0) {
              isValue = true
            }
          })
        }
      })
      return isValue
    },
    editRecord(activeRecord) {
      const data = activeRecord ? { ...activeRecord } : null
      this.activeRecord = data
      this.isAddNewProcessSidebarActive = true
      this.isAddNewProcessSidebarInDisplayMode = false
    },
    displayRecord(activeRecord) {
      this.editRecord(activeRecord)
      this.isAddNewProcessSidebarInDisplayMode = true
    },
    deleteRecord(activeRecord) {
      this.$bvModal
        .msgBoxConfirm(
          "Are you sure that you want to delete this process? \nThis action will delete all related tasks.",
          {
            title: "Please Confirm",
            size: "sm",
            okVariant: "danger",
            okTitle: "Delete",
            cancelTitle: "Cancel",
            cancelVariant: "outline-secondary",
            hideHeaderClose: false,
            centered: true,
          }
        )
        .then((value) => {
          if (value) {
            this.$store.dispatch("process/removeProcess", activeRecord.id)
          }
        })
    },
    openImportCsvModal() {
      this.$nextTick(() => {
        this.$bvModal.show("modal-import-csv")
      })
    },
    async exportCsv() {
      try {
        const queryParams = {
          q: this.selectedQueries.map((q) => q.text || q),
          offset: 0,
          limit: 1000000,
          sortBy: this.sortBy,
          isDesc: this.isSortDirDesc,
          onlyMine: this.showMine,
        }
        const csvContent = await this.$store.dispatch(
          "process/exportProcesses",
          queryParams
        )
        if (csvContent) {
          const blob = new Blob([csvContent], {
            type: "text/tab-separated-values;charset=utf-8;",
          })
          const now = new Date()
          const year = now.getFullYear()
          const month = String(now.getMonth() + 1).padStart(2, "0")
          const day = String(now.getDate()).padStart(2, "0")
          const hours = String(now.getHours()).padStart(2, "0")
          const minutes = String(now.getMinutes()).padStart(2, "0")
          const seconds = String(now.getSeconds()).padStart(2, "0")
          const timestamp = `${year}-${month}-${day}-${hours}-${minutes}-${seconds}`

          const link = document.createElement("a")
          const url = URL.createObjectURL(blob)
          link.setAttribute("href", url)
          link.setAttribute("download", `task_templates_${timestamp}.tsv`)
          link.style.visibility = "hidden"
          document.body.appendChild(link)
          link.click()
          document.body.removeChild(link)
        }
      } catch (error) {
        console.error("Export failed:", error)
      }
    },
    isItemSelected(record) {
      return !!this.selectedItems.find((e) => e.id === record.id)
    },
    selectOrUnselectRow(checked, record) {
      if (checked) {
        if (!this.isItemSelected(record)) {
          this.selectedItems.push(record)
        }
      } else {
        const recordToDeleteIndex = this.selectedItems.findIndex(
          (e) => e.id === record.id
        )
        if (recordToDeleteIndex !== -1) {
          this.selectedItems.splice(recordToDeleteIndex, 1)
        }
      }
    },
    selectAllRows() {
      this.selectedItems = [...this.allRawProcesses]
    },
    clearSelected() {
      this.selectedItems = []
    },
    selectOrUnselectAllRows(value) {
      if (value) this.selectAllRows()
      else this.clearSelected()
    },
    deleteSelectedRecords() {
      this.$bvModal
        .msgBoxConfirm(
          `Are you sure that you want to delete ${this.selectedItems.length} selected process${this.selectedItems.length === 1 ? '' : 'es'}? \nThis action will delete all related tasks.`,
          {
            title: "Please Confirm",
            size: "sm",
            okVariant: "danger",
            okTitle: "Delete",
            cancelTitle: "Cancel",
            cancelVariant: "outline-secondary",
            hideHeaderClose: false,
            centered: true,
          }
        )
        .then((value) => {
          if (value) {
            const selectedItemsIds = this.selectedItems.map((item) => item.id)
            this.$store
              .dispatch("process/removeProcesses", selectedItemsIds)
              .then(() => {
                this.selectedItems = []
              })
          }
        })
    },
    clearQuery() {
      this.$router.replace({ query: null })
    },
    onImportSuccess() {
      this.refetchData()
    },
  },
}
</script>

<style lang="scss" scoped>
@import "../../@core/scss/vue/libs/vue-select";

.container-card {
  background-color: white;
  border: none;
  box-shadow: 0 4px 24px 0 rgba(34, 41, 47, 0.08);
  border-radius: 0.428rem;
  overflow: hidden;
}

.per-page-selector {
  width: 90px;
}

.toolbar-container {
  row-gap: 0.5rem;
}

.toolbar-left {
  min-width: 0;
}

.filter-wrapper {
  min-width: 260px;
  flex: 1 1 auto;
}

.category-group-header {
  display: flex;
  align-items: center;
  padding: 0.75rem 1rem;
  background-color: rgba(25, 58, 154, 0.08);
  border-top: 1px solid rgba(25, 58, 154, 0.15);
  border-bottom: 1px solid rgba(25, 58, 154, 0.15);
  cursor: pointer;
  user-select: none;
  transition: background-color 0.15s ease;

  &:hover {
    background-color: rgba(25, 58, 154, 0.14);
  }

  &.is-first {
    border-top: none;
  }
}

.category-prefix-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background-color: #193a9a;
  color: #ffffff;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.5px;
  padding: 0.15rem 0.45rem;
  border-radius: 4px;
  min-width: 28px;
  text-align: center;
}

.category-title {
  font-size: 0.95rem;
  font-weight: 700;
  color: #2e384d;
}

.category-count {
  font-size: 0.82rem;
  font-weight: 500;
  color: #5e5873;
}

.role-chip {
  display: inline-flex;
  align-items: center;
  background-color: #f1f2f4;
  border: 1px solid #dcdfe4;
  border-radius: 4px;
  padding: 0.15rem 0.45rem;
  font-size: 0.78rem;
  font-weight: 600;
  color: #5e5873;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  white-space: nowrap;
}

.frequency-plain-text {
  font-size: 0.85rem;
  font-weight: 500;
  color: #5e5873;
}

.resource-preview-popover {
  max-width: 280px;
}

.resource-popover-content {
  padding: 0.25rem 0;
}

.resource-item-link {
  color: #2e384d;
  &:hover {
    color: #7367f0;
    text-decoration: underline !important;
  }
}

.show-more-row {
  background-color: #fafafc;
  color: #193a9a;
  padding: 0.5rem 1rem;
  cursor: pointer;
  user-select: none;
  font-size: 0.82rem;
  font-weight: 600;
  transition: background-color 0.15s ease, color 0.15s ease;

  &:hover {
    background-color: rgba(25, 58, 154, 0.08);
    color: #162a56;
    text-decoration: underline;
  }
}

.row-actions-dropdown {
  ::v-deep .dropdown-toggle {
    padding: 0;
    border: none;
    background: transparent !important;
    box-shadow: none !important;
    &::after {
      display: none;
    }
  }
}
</style>

<style lang="scss">
.process-table {
  margin-bottom: 0;

  thead th {
    font-size: 0.75rem;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: #6e6b7b;
    border-top: none;
    border-bottom: 1px solid #ebe9f1;
    padding: 0.75rem 1rem !important;
    background-color: #ffffff;
  }

  tbody tr {
    cursor: pointer;
    transition: background-color 0.15s ease;

    &:hover {
      background-color: #fafafc !important;
    }

    td {
      padding: 0.75rem 1rem !important;
      vertical-align: middle !important;
      border-top: 1px solid #ebe9f1;
    }
  }

  .select-th, .select-td {
    width: 40px !important;
    max-width: 40px !important;
    padding-right: 0 !important;
  }

  .id-th, .id-td {
    width: 80px !important;
    max-width: 90px !important;
  }

  .name-th, .name-td {
    min-width: 280px !important;
  }

  .resources-th, .resources-td {
    width: 40px !important;
    max-width: 45px !important;
    padding-left: 0 !important;
    padding-right: 0 !important;
    text-align: center;
  }

  .frequency-th, .frequency-td {
    width: 180px !important;
    max-width: 200px !important;
  }

  .role-th, .role-td {
    width: 100px !important;
    max-width: 120px !important;
  }

  .labels-th, .labels-td {
    min-width: 180px !important;
  }

  .actions-th, .actions-td {
    width: 40px !important;
    max-width: 40px !important;
    text-align: right;
    padding-right: 1.25rem !important;
  }
}

.dark-layout {
  .category-group-header {
    background-color: rgba(25, 58, 154, 0.2);
    border-color: rgba(25, 58, 154, 0.35);
    &:hover {
      background-color: rgba(25, 58, 154, 0.3);
    }
  }
  .category-title {
    color: #d0d2d6;
  }
  .category-count {
    color: #b4b7bd;
  }
  .show-more-row {
    background-color: #161d31;
    color: #7367f0;
    border-color: #3b4253;
    &:hover {
      background-color: #283046;
      color: #9087f4;
    }
  }
  .role-chip {
    background-color: #283046;
    border-color: #3b4253;
    color: #d0d2d6;
  }
  .frequency-plain-text {
    color: #d0d2d6;
  }
  .container-card {
    background-color: #283046;
  }
  .process-table {
    thead th {
      background-color: #283046;
      border-color: #3b4253;
      color: #b4b7bd;
    }
    tbody tr td {
      border-color: #3b4253;
    }
    tbody tr:hover {
      background-color: #161d31 !important;
    }
  }
}
</style>
